import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Flame, Sparkles, Zap, Shield, Trophy, ArrowRight, Gift, Crown } from "lucide-react";
import Logo from "../components/Logo";
import AuthModal from "../components/AuthModal";

const HERO = "https://images.unsplash.com/photo-1647524592742-34924d206a9e?auto=format&fit=crop&w=1600&q=70";

const REWARD_SHOWCASE = [
  { img: "https://images.unsplash.com/photo-1606220588913-b3aacb4d2f46?auto=format&fit=crop&w=600&q=70", label: "Wireless Earbuds", credits: "500k" },
  { img: "https://images.unsplash.com/photo-1579586337278-3befd40fd17a?auto=format&fit=crop&w=600&q=70", label: "Smartwatch", credits: "1.8M" },
  { img: "https://images.unsplash.com/photo-1546435770-a3e426bf472b?auto=format&fit=crop&w=600&q=70", label: "Apple Watch / Sony", credits: "3M" },
  { img: "https://images.unsplash.com/photo-1592750475338-74b7b21085ab?auto=format&fit=crop&w=600&q=70", label: "iPhone 17", credits: "8M" },
  { img: "https://images.unsplash.com/photo-1593359677879-a4bb92f829d1?auto=format&fit=crop&w=600&q=70", label: "Smart TV / MacBook", credits: "12M" },
];

export default function Landing() {
  const navigate = useNavigate();
  const [hovered, setHovered] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState("signin");

  const openAuth = (mode) => {
    setAuthMode(mode);
    setAuthOpen(true);
  };

  return (
    <div data-testid="landing-page" className="min-h-screen relative overflow-hidden">
      {/* Hero background */}
      <div className="absolute inset-0">
        <img src={HERO} alt="" className="w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(5,5,8,0.5), rgba(5,5,8,0.92))" }} />
      </div>

      <div className="relative max-w-5xl mx-auto px-5 pt-8 pb-24">
        {/* Nav */}
        <div className="flex items-center justify-between">
          <Logo size={44} />
          <button
            data-testid="nav-login-btn"
            onClick={() => openAuth("signin")}
            className="px-4 py-2 rounded-full text-sm font-semibold glass hover:bg-white/10"
          >
            Sign in
          </button>
        </div>

        {/* Hero */}
        <div className="mt-16 md:mt-28">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="chip inline-flex" style={{ background: "#B026FF22", color: "#B026FF", border: "1px solid #B026FF55" }}
          >
            <Sparkles className="w-3 h-3" /> India's #1 fan fantasy game · 100% free
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="font-display mt-4 text-5xl sm:text-6xl lg:text-7xl font-bold leading-[0.95] tracking-tighter"
          >
            Pick your stars.<br />
            <span className="neon-brand">Win an iPhone 17.</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.25 }}
            className="mt-6 max-w-xl text-base sm:text-lg text-white/75"
          >
            Pick 6 stars. Crown a Captain. Outscore your friends. Climb the contest leaderboard and grow your{" "}
            <b className="text-white">Fanpuff Credits</b> wallet — every milestone unlocks{" "}
            <b className="text-[#FFD600]">a real reward</b>: BookMyShow vouchers, boAt earbuds, Fire-Boltt smartwatches,{" "}
            AirPods, even <b className="text-white">an iPhone 17</b> or <b className="text-white">4K Smart TV</b>.<br />
            <span className="text-white/55">No deposits. No withdrawals. Just clout, culture, and stuff you'd actually flex.</span>
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="mt-8 flex flex-wrap gap-3"
          >
            <button
              data-testid="landing-get-started"
              onClick={() => openAuth("signup")}
              onMouseEnter={() => setHovered(true)}
              onMouseLeave={() => setHovered(false)}
              className="group inline-flex items-center gap-2 px-6 py-3 rounded-full text-black font-bold tracking-wide"
              style={{ background: "linear-gradient(90deg, #00F0FF, #B026FF)", boxShadow: hovered ? "0 0 40px rgba(176,38,255,0.6)" : "0 0 22px rgba(176,38,255,0.35)" }}
            >
              Claim 1,00,000 free credits
              <ArrowRight className="w-4 h-4 transition group-hover:translate-x-0.5" />
            </button>
            <a
              href="#rewards"
              className="px-6 py-3 rounded-full glass font-semibold text-white/85 hover:bg-white/10"
            >
              See what you can win
            </a>
          </motion.div>

          <div className="mt-10 flex flex-wrap items-center gap-2 text-xs text-white/50">
            <span className="chip" style={{ background: "#00FF6615", color: "#00FF66", border: "1px solid #00FF6644" }}>
              ⚡ 60-second signup
            </span>
            <span className="chip" style={{ background: "#FFD60015", color: "#FFD600", border: "1px solid #FFD60044" }}>
              🎁 Real rewards · Zero cash
            </span>
            <span className="chip" style={{ background: "#00F0FF15", color: "#00F0FF", border: "1px solid #00F0FF44" }}>
              🇮🇳 18+ · Made for fandoms
            </span>
          </div>
        </div>

        {/* Social proof / scarcity strip */}
        <div className="mt-10 grid grid-cols-3 gap-3" data-testid="landing-stats-strip">
          <StatBubble num="237" label="Stars to back" color="#00F0FF" />
          <StatBubble num="6" label="Contest tiers" color="#FFD600" />
          <StatBubble num="9" label="Reward unlocks" color="#00FF66" />
        </div>

        {/* Reward Showcase strip */}
        <div id="rewards" className="mt-20" data-testid="landing-reward-showcase">
          <div className="flex items-center gap-2 mb-4">
            <Gift className="w-5 h-5 text-[#FFD600]" />
            <p className="text-xs uppercase tracking-[0.3em] font-bold text-white/55">Rewards waiting for you</p>
          </div>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight leading-tight mb-2">
            Play. Win credits. <span className="neon-brand">Unlock real stuff.</span>
          </h2>
          <p className="text-sm text-white/55 mb-5 max-w-2xl">
            Every contest you win bumps your wallet up the ladder. The bigger your wallet, the bigger the drop you can claim — sent to your door. No gachas, no real money, no catch.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {REWARD_SHOWCASE.map((r) => (
              <div key={r.label} className="rounded-2xl overflow-hidden glass card-hover relative">
                <div className="aspect-square relative">
                  <img src={r.img} alt={r.label} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, transparent 30%, rgba(5,5,8,0.92))" }} />
                  <div className="absolute bottom-2.5 left-2.5 right-2.5">
                    <p className="text-[11px] font-display font-bold leading-tight">{r.label}</p>
                    <p className="text-[10px] text-[#FFD600] font-mono-brand">@ {r.credits} credits</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-white/40 mt-3 leading-relaxed max-w-2xl">
            9 reward tiers · 150k → 12M Fanpuff Credits · Premium tiers (3M+) are sponsor-backed with limited monthly inventory. First in, first served.
          </p>
        </div>

        {/* How it works */}
        <div id="how" className="mt-20">
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight leading-tight">
            Three minutes in. <span className="neon-brand">Hooked for the season.</span>
          </h2>
          <p className="text-sm text-white/55 mt-2 max-w-2xl">
            No fantasy stats homework. No salary caps. Just gut-feel picks on the stars you already follow on Instagram.
          </p>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-5">
            <Feature
              icon={Crown}
              color="#FFD600"
              title="Pick 6. Crown 1. Done."
              body="Bollywood, creators, reality TV, music & athletes. Crown your Captain (2× points), Vice (1.5×). The rest is vibes — and which star is hot this week."
            />
            <Feature
              icon={Trophy}
              color="#B026FF"
              title="Win up to 50% of the pool"
              body="Top finishers split a 70% return prize pool. The bigger the contest, the fatter the haul. Mini Daily 2k entry → Season Cup 50k entry. Your call."
            />
            <Feature
              icon={Gift}
              color="#00FF66"
              title="Cash out the reward, not cash"
              body="Wallet at 500k? Claim wireless earbuds. 1.8M? Smartwatch on the way. 8M? iPhone 17. We ship it. Free. Promotional & limited stock — race the leaderboard."
            />
            <Feature
              icon={Flame}
              color="#FF0055"
              title="Heat — your inside scoop"
              body="A live signal of who's rising right now. See viral spikes, music drops, scandal moments before everyone else and back the dark horse first."
            />
            <Feature
              icon={Sparkles}
              color="#00F0FF"
              title="1v1 Friend Duels"
              body="Send a duel link to your group chat. Both pick 6, top points wins. Bragging rights immediately. Saves itself as a meme — guaranteed reshare."
            />
            <Feature
              icon={Shield}
              color="#B026FF"
              title="Not a casino. Promise."
              body="100% free. Zero deposits. Zero withdrawals. Fanpuff Credits have no cash value — they're just your unlock key for real rewards. Built for fandoms, not gamblers."
            />
          </div>
        </div>

        {/* Final CTA */}
        <div className="mt-20" data-testid="landing-final-cta">
          <div className="rounded-3xl p-7 sm:p-10 glass-strong relative overflow-hidden text-center">
            <div aria-hidden className="pointer-events-none absolute -top-20 left-1/2 -translate-x-1/2 w-72 h-72 rounded-full opacity-30"
              style={{ background: "radial-gradient(circle,#B026FF,transparent 70%)" }} />
            <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#FFD600]/85">Limited monthly drops · India only</p>
            <h2 className="font-display mt-2 text-3xl sm:text-4xl font-bold leading-tight">
              Why are you still scrolling?
            </h2>
            <p className="mt-3 text-sm sm:text-base text-white/65 max-w-md mx-auto">
              Join 12,000+ Indian Gen-Z fans already racing for the next iPhone drop. 60-second signup. Zero card details. Just pick and ship.
            </p>
            <button
              onClick={() => openAuth("signup")}
              data-testid="landing-final-cta-btn"
              className="mt-6 inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-black font-bold tracking-wide"
              style={{ background: "linear-gradient(90deg,#00F0FF,#B026FF)", boxShadow: "0 0 30px rgba(176,38,255,0.4)" }}
            >
              Claim 1,00,000 free credits <ArrowRight className="w-4 h-4" />
            </button>
            <p className="mt-3 text-[10px] text-white/40">
              No deposits. No withdrawals. Promotional rewards subject to eligibility.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-16 flex flex-col items-center gap-3 text-center text-xs text-white/40 max-w-lg mx-auto">
          <p className="leading-relaxed">
            Fanpuff is a free-to-play entertainment fantasy experience. Fanpuff Credits have no cash value and cannot be withdrawn. No deposits, no withdrawals, no real-money gaming. Rewards are promotional and subject to eligibility, availability, and platform rules.
          </p>
          <p>© {new Date().getFullYear()} Fanpuff — Made for fandoms.</p>
        </div>
      </div>
      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} initialMode={authMode} />
    </div>
  );
}

function Feature({ icon: Icon, color, title, body }) {
  return (
    <div className="rounded-3xl p-6 glass card-hover">
      <div className="w-11 h-11 rounded-2xl grid place-items-center" style={{ background: `${color}18`, border: `1px solid ${color}55` }}>
        <Icon className="w-5 h-5" style={{ color }} />
      </div>
      <h3 className="mt-4 font-display text-xl font-semibold">{title}</h3>
      <p className="mt-2 text-sm text-white/65 leading-relaxed">{body}</p>
    </div>
  );
}

function StatBubble({ num, label, color }) {
  return (
    <div className="rounded-2xl p-4 text-center glass" style={{ border: `1px solid ${color}33` }}>
      <p className="font-display text-3xl font-bold" style={{ color }}>{num}</p>
      <p className="text-[10px] uppercase tracking-widest text-white/55 font-bold mt-1">{label}</p>
    </div>
  );
}
